import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

function ProductSubscriptionManager() {
  // States
  const [activeTab, setActiveTab] = useState('Large box');
  const [showModal, setShowModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [subscriptions, setSubscriptions] = useState({
    'Large box': [],
    'Small box': []
  });
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [subscriptionType, setSubscriptionType] = useState('Large box');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    brand: '',
    price: '',
    quantity: 1,
    description: ''
  });

  const token = localStorage.getItem("adminToken");

  // Fetch all products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get('https://afric-fresh-backend.onrender.com/admin/product-subscrition', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await axios.get('https://afric-fresh-backend.onrender.com/admin/subscrition/categories', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    const fetchSubscriptions = async () => {
      try {
        const largeBoxResponse = await axios.get('https://afric-fresh-backend.onrender.com/admin/subscriptions/Large box', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const smallBoxResponse = await axios.get('https://afric-fresh-backend.onrender.com/admin/subscriptions/Small box', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setSubscriptions({
          'Large box': largeBoxResponse.data,
          'Small box': smallBoxResponse.data
        });
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
      }
    };

    fetchProducts();
    fetchCategories();
    fetchSubscriptions();
  }, []);

  // Filtered products based on category and search term
  const filteredProducts = () => {
    let filtered = products;
    
    if (activeCategory !== 'All') {
      filtered = filtered.filter(product => product.category === activeCategory);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.price.includes(searchTerm)
      );
    }
    
    return filtered;
  };

  // Handle selection of products
  const toggleProductSelection = (product) => {
    // Don't allow selection if product is out of stock
    if (product.quantity <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Out of Stock',
        text: `"${product.name}" is out of stock and cannot be added to subscriptions. Please restock first.`,
        timer: 3000
      });
      return;
    }

    if (selectedProducts.some(p => p.id === product.id)) {
      setSelectedProducts(selectedProducts.filter(p => p.id !== product.id));
    } else {
      setSelectedProducts([...selectedProducts, product]);
    }
  };

  // Save selected products to subscription
  const saveSelection = async () => {
    if (selectedProducts.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'No Products Selected',
        text: 'Please select at least one product.'
      });
      return;
    }

    // Check for any out-of-stock products in selection
    const outOfStockProducts = selectedProducts.filter(p => p.quantity <= 0);
    if (outOfStockProducts.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Out of Stock Products',
        text: `Cannot add ${outOfStockProducts.length} out-of-stock product(s) to subscriptions.`,
        timer: 5000
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        'https://afric-fresh-backend.onrender.com/admin/subscriptions',
        {
          products: selectedProducts,
          subscriptionType
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Refresh subscriptions
      const response = await axios.get(
        `https://afric-fresh-backend.onrender.com/admin/subscriptions/${subscriptionType}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setSubscriptions({
        ...subscriptions,
        [subscriptionType]: response.data
      });

      // Clear selection
      setSelectedProducts([]);
      setShowModal(false);
      
      Swal.fire({
        icon: 'success',
        title: 'Products Added!',
        text: `Products have been added to ${subscriptionType} successfully!`
      });
    } catch (error) {
      console.error('Error adding products to subscription:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to add products to subscription.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete subscription
  const deleteSubscription = async (id) => {
    try {
      await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      }).then(async (result) => {
        if (result.isConfirmed) {
          await axios.delete(
            `https://afric-fresh-backend.onrender.com/admin/subscriptions/${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          
          // Refresh subscriptions
          const response = await axios.get(
            `https://afric-fresh-backend.onrender.com/admin/subscriptions/${activeTab}`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          setSubscriptions({
            ...subscriptions,
            [activeTab]: response.data
          });
          
          Swal.fire(
            'Deleted!',
            'Your product has been removed from subscription.',
            'success'
          );
        }
      });
    } catch (error) {
      console.error('Error deleting subscription:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete product from subscription.'
      });
    }
  };

  // Start editing subscription
  const startEdit = (subscription) => {
    setEditingSubscription(subscription._id);
    setEditFormData({
      name: subscription.name,
      brand: subscription.brand,
      price: subscription.price,
      quantity: subscription.quantity,
      description: subscription.description
    });
  };

  // Handle edit form changes
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };

  // Save edited subscription
  const saveEdit = async (id) => {
    try {
      await axios.put(
        `https://afric-fresh-backend.onrender.com/admin/subscriptions/${id}`,
        editFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Refresh subscriptions
      const response = await axios.get(
        `https://afric-fresh-backend.onrender.com/admin/subscriptions/${activeTab}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setSubscriptions({
        ...subscriptions,
        [activeTab]: response.data
      });
      
      setEditingSubscription(null);
      Swal.fire({
        icon: 'success',
        title: 'Updated!',
        text: 'Product details have been updated.'
      });
    } catch (error) {
      console.error('Error updating subscription:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update product details.'
      });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <style>{`
        .out-of-stock-overlay {
          position: absolute;
          inset: 0;
          background-color: rgba(239, 68, 68, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }
        .out-of-stock-badge {
          background-color: #ef4444;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
        }
      `}</style>
      
      <h1 className="text-3xl font-bold text-center mb-8">Product Subscription Manager</h1>
      
      {/* Main Tabs */}
      <div className="flex justify-center mb-6">
        <div className="flex rounded-md overflow-hidden border border-gray-300">
          <button
            className={`px-6 py-3 font-medium ${activeTab === 'Large box' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
            onClick={() => setActiveTab('Large box')}
          >
            Large Box
          </button>
          <button
            className={`px-6 py-3 font-medium ${activeTab === 'Small box' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
            onClick={() => setActiveTab('Small box')}
          >
            Small Box
          </button>
        </div>
      </div>
      
      {/* Subscription Box Content */}
      <div className="mb-12">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold">{activeTab} Products</h2>
          
          <button
            onClick={() => setShowModal(true)}
            className="bg-green-500 hover:bg-green-600 text-white font-medium rounded-full p-4 flex items-center justify-center shadow-lg transition duration-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
        
        {/* Subscription products grid */}
        {subscriptions[activeTab].length === 0 ? (
          <div className="bg-gray-100 rounded-lg p-8 text-center">
            <p className="text-gray-500 text-lg">No products added to {activeTab} yet.</p>
            <button
              onClick={() => setShowModal(true)}
              className="mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
            >
              Add Products
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {subscriptions[activeTab].map(subscription => (
              <div key={subscription._id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                <div className="h-48 overflow-hidden relative">
                  {subscription.img ? (
                    <img src={subscription.img} alt={subscription.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-400">No image</span>
                    </div>
                  )}
                  {subscription.quantity <= 0 && (
                    <div className="out-of-stock-overlay">
                      <span className="out-of-stock-badge">OUT OF STOCK</span>
                    </div>
                  )}
                </div>
                
                <div className="p-4">
                  {editingSubscription === subscription._id ? (
                    // Edit Form
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-600">Name</label>
                        <input
                          type="text"
                          name="name"
                          value={editFormData.name}
                          onChange={handleEditChange}
                          className="w-full p-2 border rounded mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600">Brand</label>
                        <input
                          type="text"
                          name="brand"
                          value={editFormData.brand}
                          onChange={handleEditChange}
                          className="w-full p-2 border rounded mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600">Price</label>
                        <input
                          type="text"
                          name="price"
                          value={editFormData.price}
                          onChange={handleEditChange}
                          className="w-full p-2 border rounded mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600">Quantity</label>
                        <input
                          type="number"
                          name="quantity"
                          value={editFormData.quantity}
                          onChange={handleEditChange}
                          min="1"
                          className="w-full p-2 border rounded mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600">Description</label>
                        <textarea
                          name="description"
                          value={editFormData.description}
                          onChange={handleEditChange}
                          className="w-full p-2 border rounded mt-1"
                          rows="3"
                        ></textarea>
                      </div>
                      <div className="flex space-x-2 pt-2">
                        <button
                          onClick={() => saveEdit(subscription._id)}
                          className="bg-green-500 text-white px-3 py-1 rounded"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingSubscription(null)}
                          className="bg-gray-300 px-3 py-1 rounded"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Display Mode
                    <>
                      <h3 className="font-bold text-lg">{subscription.name}</h3>
                      <p className="text-gray-600">{subscription.brand}</p>
                      <p className="text-blue-600 font-semibold mt-2">{subscription.price}</p>
                      <p className={`mt-1 text-sm ${
                        subscription.quantity <= 0 ? 'text-red-500 font-bold' : ''
                      }`}>
                        Quantity: {subscription.quantity}
                      </p>
                      <div className="mt-2 bg-gray-50 p-2 rounded-md">
                        <p className="text-sm text-gray-700">{subscription.description.substring(0, 100)}
                          {subscription.description.length > 100 ? '...' : ''}
                        </p>
                      </div>
                      <p className="mt-2 inline-block px-2 py-1 bg-gray-200 rounded-full text-xs">{subscription.category}</p>
                      
                      <div className="flex justify-between mt-4 pt-2 border-t">
                        <button
                          onClick={() => startEdit(subscription)}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteSubscription(subscription._id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Product Selection Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b sticky top-0 bg-white z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Select Products</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Filter Controls - Fixed at the top */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mt-4 pb-4 border-b">
                <div className="flex flex-wrap gap-2">
                  <button 
                    onClick={() => setActiveCategory('All')}
                    className={`px-3 py-1 rounded-full text-sm ${activeCategory === 'All' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                  >
                    All
                  </button>
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => setActiveCategory(category)}
                      className={`px-3 py-1 rounded-full text-sm ${activeCategory === category ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
                
                <div className="flex gap-3 items-center">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border rounded p-2 w-full md:w-48"
                  />
                  
                  <select
                    value={subscriptionType}
                    onChange={(e) => setSubscriptionType(e.target.value)}
                    className="border rounded p-2"
                  >
                    <option value="Large box">Large box</option>
                    <option value="Small box">Small box</option>
                  </select>
                  
                  <button
                    onClick={saveSelection}
                    disabled={loading || selectedProducts.length === 0}
                    className={`bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded flex items-center gap-2 ${(loading || selectedProducts.length === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>Save Selection</>
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {/* Products Grid - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4">
              {filteredProducts().length === 0 ? (
                <div className="text-center py-8">
                  <p>No products found.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredProducts().map(product => {
                    const isSelected = selectedProducts.some(p => p.id === product.id);
                    const isOutOfStock = product.quantity <= 0;

                    return (
                      <div 
                        key={product.id}
                        className={`border rounded-lg overflow-hidden relative ${
                          isSelected ? 'border-blue-500 ring-2 ring-blue-300' : 'border-gray-200'
                        } ${isOutOfStock ? 'opacity-70' : ''}`}
                      >
                        {/* Out of stock overlay */}
                        {isOutOfStock && (
                          <div className="out-of-stock-overlay">
                            <span className="out-of-stock-badge">OUT OF STOCK</span>
                          </div>
                        )}
                        
                        <div className="h-40 overflow-hidden bg-gray-100">
                          {product.img ? (
                            <img src={product.img} alt={product.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-gray-400">No image</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="p-3">
                          <h3 className="font-semibold truncate">{product.name}</h3>
                          <p className="text-gray-600 text-sm">{product.brand}</p>
                          <p className="text-blue-600 font-medium mt-1">${product.price}</p>
                          <p className="mt-1 text-xs bg-gray-100 inline-block px-2 py-0.5 rounded">
                            {product.category}
                          </p>
                          
                          {/* Stock status */}
                          <p className={`text-xs mt-1 ${
                            isOutOfStock ? 'text-red-500 font-bold' : 'text-gray-500'
                          }`}>
                            {isOutOfStock ? 'Out of stock' : `${product.quantity} available`}
                          </p>
                          
                          <button
                            onClick={() => toggleProductSelection(product)}
                            disabled={isOutOfStock}
                            className={`mt-3 w-full py-1.5 rounded ${
                              isSelected ? 'bg-blue-500 text-white' : 
                              isOutOfStock ? 'bg-gray-300 cursor-not-allowed' : 'bg-gray-200 text-gray-800'
                            }`}
                          >
                            {isSelected ? 'Selected' : 
                             isOutOfStock ? 'Out of Stock' : 'Select Product'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductSubscriptionManager;